<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="visible"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        @click.self="$emit('close')"
      >
        <div
          class="plan-limit-modal relative w-full max-w-md overflow-hidden rounded-2xl shadow-2xl md:max-w-lg"
          role="dialog"
          aria-modal="true"
          aria-labelledby="plan-limit-title"
          @click.stop
        >
          <!-- Header: accent gradient + subtle pattern -->
          <div
            class="plan-limit-header relative px-6 pb-8 pt-10 text-center md:px-8 md:pb-10 md:pt-12"
          >
            <div class="plan-limit-header-pattern pointer-events-none absolute inset-0" aria-hidden="true" />

            <button
              type="button"
              @click="$emit('close')"
              class="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-lg text-white/85 transition hover:bg-white/10 hover:text-white md:right-4 md:top-4"
              aria-label="Cerrar"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>

            <div class="relative z-10">
              <div
                class="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-white/20 ring-2 ring-white/25 md:mb-6"
              >
                <RocketLaunchIcon class="h-9 w-9 text-white" />
              </div>

              <!-- Plan-gate: show which plan is required -->
              <template v-if="isPlanGate">
                <h2
                  id="plan-limit-title"
                  class="text-xl font-bold leading-tight text-white md:text-2xl"
                >
                  Activa el plan {{ requiredPlanLabel }}
                </h2>
                <p class="mt-2 hidden text-sm font-semibold uppercase tracking-widest text-white/70 md:block">
                  {{ featureLabel }} &middot; plan requerido
                </p>
              </template>
              <template v-else>
                <h2
                  id="plan-limit-title"
                  class="text-xl font-bold leading-tight text-white md:text-2xl"
                >
                  ¿Listo para el siguiente nivel?
                </h2>
              </template>

              <!-- Subtitle / API message (desktop) -->
              <p
                class="mt-3 hidden text-sm leading-relaxed text-white/95 md:block md:px-2"
              >
                {{ displayMessage }}
              </p>
            </div>
          </div>

          <!-- Body -->
          <div
            class="bg-white px-6 pb-6 pt-6 dark:bg-dark-background md:px-8 md:pb-8 md:pt-7"
          >
            <!-- Mobile: API message first -->
            <p
              class="mb-3 text-center text-sm font-medium text-light-text-primary dark:text-dark-text-primary md:hidden"
            >
              {{ displayMessage }}
            </p>

            <!-- Plan-gate: current vs required plan comparison -->
            <div
              v-if="isPlanGate && currentPlan && requiredPlan"
              class="mb-4 flex items-center justify-center gap-3 rounded-xl border border-light-border dark:border-dark-border bg-light-background dark:bg-dark-background/60 px-4 py-3"
            >
              <div class="text-center">
                <p class="text-xs font-medium uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary">
                  Tu plan actual
                </p>
                <p class="mt-0.5 text-sm font-bold text-light-text-primary dark:text-dark-text-primary">
                  {{ currentPlanLabel }}
                </p>
              </div>
              <ArrowRightIcon class="h-4 w-4 shrink-0 text-light-accent dark:text-dark-accent" />
              <div class="text-center">
                <p class="text-xs font-medium uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary">
                  Plan necesario
                </p>
                <p class="mt-0.5 text-sm font-bold text-light-accent dark:text-dark-accent">
                  {{ requiredPlanLabel }}
                </p>
              </div>
            </div>

            <p
              class="text-center text-sm leading-relaxed text-light-text-secondary dark:text-dark-text-secondary md:text-left"
            >
              <span class="md:hidden">{{ mobileBodyText }}</span>
              <span class="hidden md:inline">{{ desktopBodyText }}</span>
            </p>

            <!-- Benefits list (desktop) -->
            <ul
              class="mt-5 hidden space-y-3 text-left text-sm text-light-text-primary dark:text-dark-text-primary md:block"
            >
              <li v-for="(line, idx) in featureLines" :key="idx" class="flex gap-3">
                <span
                  class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--light-accent)_18%,transparent)] dark:bg-[color-mix(in_srgb,var(--dark-accent)_22%,transparent)]"
                >
                  <CheckIcon class="h-3.5 w-3.5 text-[var(--light-accent)] dark:text-[var(--dark-accent)]" />
                </span>
                <span>{{ line }}</span>
              </li>
            </ul>

            <button
              type="button"
              @click="$emit('goToSubscription')"
              class="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--light-accent)] py-3.5 text-base font-semibold text-[var(--light-cta-text)] shadow-lg transition hover:bg-[var(--light-accent-hover)] dark:bg-[var(--dark-accent)] dark:text-[var(--dark-cta-text)] dark:hover:opacity-95"
            >
              <template v-if="isPlanGate">
                Subir al plan {{ requiredPlanLabel }}
              </template>
              <template v-else>
                Actualizar plan ahora
              </template>
              <ArrowRightIcon class="h-5 w-5" />
            </button>

            <button
              type="button"
              @click="$emit('close')"
              class="mt-4 w-full text-center text-sm font-medium text-light-text-secondary transition hover:text-[var(--light-accent)] dark:text-dark-text-secondary dark:hover:text-[var(--dark-accent)]"
            >
              Quizás más tarde
            </button>

            <div
              class="mt-6 flex items-center justify-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-light-text-secondary/70 dark:text-dark-text-secondary/70"
            >
              <LockClosedIcon class="h-3.5 w-3.5 shrink-0 opacity-70" />
              <span class="md:hidden">Pago seguro & encriptado</span>
              <span class="hidden md:inline">Pago seguro cifrado de extremo a extremo</span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import {
  ArrowRightIcon,
  CheckIcon,
  LockClosedIcon,
  RocketLaunchIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  message: {
    type: String,
    default: 'Has alcanzado el límite de empresas de tu plan actual.'
  },
  variant: {
    type: String,
    default: 'companies',
    validator: (v) => ['companies', 'clients', 'services', 'appointments', 'subscription'].includes(v)
  },
  description: {
    type: String,
    default: ''
  },
  // Plan-gate props — populated from 402 PLAN_UPGRADE_REQUIRED responses
  feature: {
    type: String,
    default: ''
  },
  requiredPlan: {
    type: String,
    default: ''
  },
  currentPlan: {
    type: String,
    default: ''
  }
})

defineEmits(['close', 'goToSubscription'])

/** True when displaying a feature-gate (402) response with full plan data. */
const isPlanGate = computed(() => !!props.feature && !!props.requiredPlan)

const FEATURE_LABELS = {
  'reports': 'Reportes avanzados',
  'service-orders': 'Pipeline de trabajo',
  'audit-logs': 'Registros de auditoría',
  'google-calendar': 'Google Calendar',
  'legal-documents': 'Documentos legales'
}

const PLAN_LABELS = {
  free: 'Gratis',
  basic: 'Básico',
  pro: 'Pro',
  annual: 'Pro Anual'
}

const featureLabel = computed(
  () => FEATURE_LABELS[props.feature] || props.feature
)
const requiredPlanLabel = computed(
  () => PLAN_LABELS[props.requiredPlan] || props.requiredPlan
)
const currentPlanLabel = computed(
  () => PLAN_LABELS[props.currentPlan] || props.currentPlan
)

const displayMessage = computed(() => {
  const m = (props.message || '').trim()
  if (m) return m
  if (isPlanGate.value) {
    return `${featureLabel.value} requiere el plan ${requiredPlanLabel.value} o superior.`
  }
  return 'Has alcanzado un límite de tu plan actual.'
})

// Feature-specific benefit bullets
const FEATURE_GATE_BENEFITS = {
  'reports': [
    'Reportes de ingresos y citas en tiempo real',
    'Estadísticas de retención de clientes',
    'Exporta datos para análisis externo'
  ],
  'service-orders': [
    'Pipeline Kanban para gestionar órdenes de trabajo',
    'Seguimiento de estado por cada servicio',
    'Vista global de la carga del equipo'
  ],
  'audit-logs': [
    'Historial completo de cambios en la cuenta',
    'Control de seguridad y trazabilidad',
    'Identifica acciones y responsables fácilmente'
  ],
  'google-calendar': [
    'Sincronización automática con Google Calendar',
    'Sin doble agenda ni conflictos de horario',
    'Tu equipo siempre al día'
  ],
  'legal-documents': [
    'Crea y administra contratos y consentimientos',
    'Documentos firmados digitalmente por tus clientes',
    'Todo centralizado con respaldo digital'
  ]
}

const mobileBodyText = computed(() => {
  const d = (props.description || '').trim()
  if (d) return d
  if (isPlanGate.value) {
    return `Subí al plan ${requiredPlanLabel.value} para desbloquear ${featureLabel.value} y llevar tu negocio al siguiente nivel.`
  }
  switch (props.variant) {
    case 'clients':
      return 'Tu base de clientes está creciendo. Actualiza tu plan para seguir gestionando sin límites y llevar tu profesionalismo al máximo.'
    case 'services':
      return 'Tu catálogo puede crecer más. Actualiza tu plan para añadir servicios sin trabas y seguir organizando tu negocio.'
    case 'appointments':
      return 'Tu agenda está al límite. Actualiza tu plan para seguir programando citas y no perder oportunidades.'
    case 'subscription':
      return 'Renueva tu suscripción para recuperar el acceso completo y seguir gestionando tu negocio sin interrupciones.'
    default:
      return 'Tu negocio está creciendo. Actualiza tu plan para gestionar más empresas y seguir escalando con tranquilidad.'
  }
})

const desktopBodyText = computed(() => {
  const d = (props.description || '').trim()
  if (d) return d
  if (isPlanGate.value) {
    return `Con el plan ${requiredPlanLabel.value} desbloqueás ${featureLabel.value} y accedés a herramientas pensadas para negocios que quieren escalar sin límites.`
  }
  switch (props.variant) {
    case 'clients':
      return 'Tu negocio está creciendo y nosotros estamos aquí para apoyarte. Desbloquea capacidad ilimitada de clientes, analíticas avanzadas y soporte prioritario.'
    case 'services':
      return 'Lleva tu oferta al siguiente nivel. Con un plan superior podrás ampliar servicios, analizar resultados y automatizar recordatorios con más tranquilidad.'
    case 'appointments':
      return 'Más citas, mejor organización. Sube de plan para desbloquear mayor capacidad de agenda, reportes y herramientas que te ahorran tiempo.'
    case 'subscription':
      return 'Tu suscripción venció y algunas funciones están bloqueadas. Renueva ahora para seguir operando sin interrupciones y conservar todos tus datos.'
    default:
      return 'Para seguir creciendo y gestionar múltiples sedes o negocios con soltura, actualiza tu suscripción y desbloquea el siguiente nivel para tu operación.'
  }
})

const featureLines = computed(() => {
  if (isPlanGate.value && FEATURE_GATE_BENEFITS[props.feature]) {
    return FEATURE_GATE_BENEFITS[props.feature]
  }
  switch (props.variant) {
    case 'clients':
      return ['Clientes ilimitados', 'Reportes detallados de ingresos', 'Mucho más']
    case 'services':
      return ['Más servicios en catálogo', 'Mejor visibilidad de ingresos', 'Flujos listos para escalar']
    case 'appointments':
      return ['Mayor capacidad de citas', 'Calendario y seguimiento sin cuellos de botella', 'Soporte para crecer con orden']
    case 'subscription':
      return ['Recupera acceso inmediato', 'Todos tus datos siguen intactos', 'Soporte prioritario incluido']
    default:
      return ['Más empresas o sedes', 'Herramientas pensadas para equipos', 'Acompañamiento para escalar']
  }
})
</script>

<style scoped>
.plan-limit-header {
  background-color: var(--light-accent);
  background-image:
    radial-gradient(ellipse 120% 80% at 10% -20%, rgba(255, 255, 255, 0.14) 0%, transparent 55%),
    radial-gradient(ellipse 100% 60% at 90% 110%, rgba(0, 0, 0, 0.12) 0%, transparent 50%),
    radial-gradient(ellipse 70% 50% at 50% 50%, rgba(255, 255, 255, 0.06) 0%, transparent 60%);
}

.dark .plan-limit-header {
  background-color: var(--dark-accent);
  background-image:
    radial-gradient(ellipse 120% 80% at 10% -20%, rgba(255, 255, 255, 0.1) 0%, transparent 55%),
    radial-gradient(ellipse 100% 60% at 90% 110%, rgba(0, 0, 0, 0.25) 0%, transparent 50%),
    radial-gradient(ellipse 70% 50% at 50% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 60%);
}

.plan-limit-header-pattern {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200' viewBox='0 0 400 200' fill='none'%3E%3Cpath d='M0 80c80-40 160 40 240 20s120-60 160-20v120H0V80z' fill='%23ffffff' fill-opacity='0.06'/%3E%3Cpath d='M0 120c100 30 200-30 300 0s100 50 100 80H0v-80z' fill='%23000000' fill-opacity='0.05'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: bottom;
  background-size: 100% auto;
  opacity: 0.9;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active .plan-limit-modal,
.modal-leave-active .plan-limit-modal {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .plan-limit-modal,
.modal-leave-to .plan-limit-modal {
  opacity: 0;
  transform: scale(0.96) translateY(8px);
}
</style>
